/**
 * WorkOS OAuth callback handler component
 * Enhanced to check organization status and redirect to onboarding if needed
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '@/store';
import { apiEndpoints } from '@/lib/api';

export function AuthCallback() {
  const navigate = useNavigate();
  const login = useStore((state) => state.login);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Extract JWT from URL fragment (not query params)
        const hash = window.location.hash.substring(1);
        const params = new URLSearchParams(hash);
        const token = params.get('token');
        const returnTo = params.get('returnTo');

        if (!token) {
          setError('No authentication token received');
          return;
        }

        // Decode JWT to get user info
        const payload = JSON.parse(atob(token.split('.')[1]));

        // Store in memory only (not localStorage)
        const user = {
          id: payload.sub,
          email: payload.email,
          organizationId: payload.organizationId,
          emailVerified: true, // Assume verified if they completed WorkOS auth
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        // Calculate expiresIn from exp timestamp
        const expiresIn = payload.exp ? payload.exp - Math.floor(Date.now() / 1000) : 900;
        login(user, token, expiresIn);

        // Clear URL fragment
        window.history.replaceState(null, '', window.location.pathname);

        // Check if user has an organization
        try {
          await apiEndpoints.organizations.getCurrent();
          // User has an organization, proceed to dashboard or returnTo
          navigate(returnTo || '/dashboard', { replace: true });
        } catch (orgError: any) {
          if (orgError?.status === 404) {
            // User doesn't have an organization yet, redirect to onboarding
            navigate('/onboarding', { replace: true });
          } else {
            // Some other error, still redirect to dashboard
            console.error('Error checking organization:', orgError);
            navigate(returnTo || '/dashboard', { replace: true });
          }
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        setError('Failed to complete authentication');
      }
    };

    handleCallback();
  }, [login, navigate]);

  // Show error state
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="w-full max-w-md p-6">
          <div className="rounded-lg bg-white p-6 shadow-lg dark:bg-gray-800">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900">
              <svg
                className="h-6 w-6 text-red-600 dark:text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="mt-4 text-center text-xl font-semibold text-gray-900 dark:text-gray-100">
              Authentication Failed
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">{error}</p>
            <button onClick={() => navigate('/login')} className="btn-primary mt-6 w-full">
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <div className="border-primary-600 mx-auto h-12 w-12 animate-spin rounded-full border-b-2"></div>
        <p className="mt-4 text-gray-600 dark:text-gray-400">Completing authentication...</p>
      </div>
    </div>
  );
}
